import React from 'react';

/**
 * The look of a control floating over a photo: the back, like, save and share
 * on a cover photo, and a close over a photo. Every such control reads its
 * classes from here, so the look is decided in one place. The tap area is the
 * whole 44px (w-11 h-11).
 *
 * Kurtis chose ICONS ONLY (2026-10-02), the way Home's feed cards draw their
 * like/save row: white outline icons that fill in when active, no circle, so
 * the controls stay out of the photo. Like Home's cards, the contrast comes
 * from a light scrim along the photo's edge (`PhotoControlScrim`, below): every
 * photo with controls on it renders the scrim for the edge(s) they sit on, and
 * the icon adds a tight drop shadow. (A soft glow behind each icon was tried
 * first; on a bright sky it read as a grey smudge.) The circle styles weighed
 * in the LYF Controls Lab stay below for reference.
 */
const STYLES = {
  // [&_svg]:[stroke-width:2]: Home's card icons draw at 2; the shared Icon's
  // 1.5 is too thin to read on a photo. A CSS rule beats the svg's attribute.
  icons: 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] [&_svg]:[stroke-width:2] hover:bg-white/15',
  dark: 'bg-black/45 text-white backdrop-blur-sm hover:bg-black/60',
  // text-surface-dark: #1e2632 that never flips, so the icon stays dark on the
  // white circle in dark mode too (text-content-primary would go near-white)
  white: 'bg-white/95 text-surface-dark shadow-[0_1px_2px_rgba(0,0,0,0.18),0_2px_8px_rgba(0,0,0,0.12)] hover:bg-white',
};

export const PHOTO_CONTROL_STYLE = 'icons';

export const PHOTO_CONTROL_CLASSES = STYLES[PHOTO_CONTROL_STYLE];

/**
 * The same controls when the photo box shows a pale placeholder instead of a
 * photo (a place with no cover, an empty album): white icons would vanish on
 * it, so they go dark. Use `hasPhoto ? PHOTO_CONTROL_CLASSES :
 * PLACEHOLDER_CONTROL_CLASSES`.
 */
export const PLACEHOLDER_CONTROL_CLASSES = 'text-content-primary hover:bg-black/5';

/**
 * A labelled chip over a photo ("20 photos", "1 album", "Copy", "View all 24
 * photos"). Text needs a backing an icon doesn't, so these keep dark glass
 * whatever the icon style is.
 */
export const PHOTO_LABEL_CLASSES = 'bg-black/45 text-white backdrop-blur-sm hover:bg-black/60';

/**
 * PhotoControlScrim — the light fade behind controls on a photo, along the
 * top and/or bottom edge (Home's feed cards use the same idea at the bottom).
 * Lighter and shorter than ImageScrim, which is for text: it only has to lift
 * white icons off a bright sky. Render it right after the photo, inside the
 * photo's `relative` box, so credits and controls drawn later sit above it.
 * Decorative: no pointer events, hidden from screen readers.
 */
export function PhotoControlScrim({ top = false, bottom = false, className = '' }) {
  return (
    <>
      {top && (
        <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/55 via-black/20 to-transparent ${className}`} />
      )}
      {bottom && (
        <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/55 via-black/20 to-transparent ${className}`} />
      )}
    </>
  );
}
